/**
 * Company: SPHMedia
 * Description: [Validation hook for watching user location changes on food map page]
 */

import { getRoundedCoords, isLocationInSingapore } from "@helper/utils";
import { Point, PositionError } from "@typings/foodMapTypes";
import { Dispatch, SetStateAction, useEffect } from "react";

type useWatchLocationValidationProps = {
  userPosition: Point | null;
  watchLocation: Point | undefined;
  virtualPVEvent: ReturnType<
    (typeof import("@hooks/useAnalyticsCall/useVirtualPVAnalyticsCall"))["useVirtualPVAnalyticsCall"]
  >;
  watchError: PositionError | GeolocationPositionError | null;
  setUserLocation: Dispatch<SetStateAction<Point | undefined>>;
  setWatchLocation: Dispatch<SetStateAction<Point | undefined>>;
  setGeoError: Dispatch<
    SetStateAction<PositionError | GeolocationPositionError | undefined>
  >;
};

/**
 * Custom React Hook: useWatchLocationValidationHook
 * Description: [Description of what the hook does]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useWatchLocationValidation({
  userPosition,
  watchLocation,
  virtualPVEvent,
  watchError,
  setUserLocation,
  setWatchLocation,
  setGeoError,
}: useWatchLocationValidationProps) {
  useEffect(() => {
    if (!userPosition) return;

    if (!isLocationInSingapore(userPosition)) {
      setWatchLocation(userPosition);
      setGeoError({
        code: 404,
        message: "Location out of Singapore",
      });
      return;
    }

    if (!watchLocation) {
      if (watchError) {
        setGeoError(watchError);
        setUserLocation(undefined);
      }
      return;
    }

    const { lat: roundedUserLat, lng: roundedUserLng } =
      getRoundedCoords(userPosition);
    const { lat: roundedWatchLat, lng: roundedWatchLng } =
      getRoundedCoords(watchLocation);

    const positionChanged =
      roundedUserLat !== roundedWatchLat || roundedUserLng !== roundedWatchLng;

    if (!positionChanged) return;

    // Valid update
    setUserLocation(userPosition);
    setWatchLocation(userPosition);
    virtualPVEvent(
      "/foodmap",
      "Food Map List",
      `${userPosition.lat}, ${userPosition.lng}`,
    );
  }, [
    userPosition,
    watchLocation,
    watchError,
    virtualPVEvent,
    setUserLocation,
    setWatchLocation,
    setGeoError,
  ]);
}
